import React, { useEffect, useRef, useState } from 'react';

/**
 * Decorative, auto-playing background media. Used for the landing-page hero and
 * the Pixomondo/CES section.
 *
 * Replaces the YouTube embeds these sections used to carry. Those needed scale
 * hacks (`w-[180vw] h-[180vh] scale-[1.8]`, `w-[177.78vh] min-w-[100vw]`)
 * because a YouTube iframe letterboxes at a fixed 16:9; a real <video> crops
 * correctly at any aspect ratio with `object-cover`, so the over-zoom is gone.
 *
 * Source files are pre-trimmed to their usable footage, so native `loop`
 * handles the cycle — no seeking, and no hitch waiting on a keyframe.
 *
 * The still is a permanently painted layer underneath rather than the video's
 * `poster` attribute. `poster` is only specified to show *until playback
 * begins* — what it does on a decode failure or a 404 varies by browser. A base
 * layer is deterministic and covers every case with one mechanism: slow load,
 * autoplay refused (iOS Low Power Mode), network or decode error, and
 * reduced-motion.
 */

interface BackgroundVideoProps {
  /** MP4 (H.264). Must be pre-trimmed: playback relies on native looping. */
  videoSrc: string;
  /** Shown while loading, and instead of the video whenever it cannot play. */
  stillSrc: string;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(REDUCED_MOTION_QUERY).matches;

const BackgroundVideo: React.FC<BackgroundVideoProps> = ({ videoSrc, stillSrc }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  const [showVideo, setShowVideo] = useState(false);
  /* iOS Low Power Mode refuses autoplay but does NOT set prefers-reduced-motion,
     so the element still mounts and iOS paints its native start-playback button
     over the still. That button lives in the media-controls shadow DOM, which
     element opacity does not suppress — so drop the element entirely once we
     know playback was refused. */
  const [autoplayRefused, setAutoplayRefused] = useState(false);

  /* Honour a change made while the page is open, not just the value at mount. */
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => {
      setReduceMotion(mq.matches);
      if (mq.matches) setShowVideo(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;

    /* Safari does not reliably honour the muted *attribute* for autoplay
       eligibility; the property is what it checks. */
    video.muted = true;

    /* The bare autoplay attribute fails silently when blocked. Calling play()
       explicitly gives the browser the clearest possible signal; if it is
       refused anyway the `playing` event never fires and the still stays up. */
    video.play().catch(() => {
      /* Refused — e.g. iOS Low Power Mode. Unmount the element so iOS cannot
         paint a play button over the still. */
      setAutoplayRefused(true);
    });
  }, [reduceMotion, videoSrc]);

  return (
    <>
      <img
        src={stillSrc}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Not rendered at all under reduced motion, so those users never pay the
          download — `preload="none"` would still cost a request. Likewise once
          autoplay has been refused. */}
      {!reduceMotion && !autoplayRefused && (
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden
          tabIndex={-1}
          onPlaying={() => setShowVideo(true)}
          onError={() => setShowVideo(false)}
          /* The two media-controls rules suppress the native start-playback
             button immediately. Unmounting on refusal (above) is the definitive
             fix, but play() rejects asynchronously, so without these the button
             can flash over the still for a frame or two first. */
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-700 [&::-webkit-media-controls]:!hidden [&::-webkit-media-controls-start-playback-button]:!hidden ${
            showVideo ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </>
  );
};

export default BackgroundVideo;
